import { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { api } from '../api';
import {
  PageHeader, Card, Stat, Pill, Empty, Hint, Field, Select, SourceNote,
  fmtR, fmtPct, fmtH,
} from '../components/ui';
import FilterBar from '../components/FilterBar';
import { BillingEntityFilter, BillingEntityBadge } from '../components/BillingEntity';

const KPI_SOURCES = [
  { source: 'Magnetic', label: 'Commercial value', detail: 'project once-off + monthly retainer amounts' },
  { source: 'Magnetic', label: 'Tracked hours', detail: 'logged time per project, feeds cost' },
  { source: 'BambooHR', label: 'Cost-to-company', detail: 'monthly salary → hourly cost rate (salary ÷ 160). Estimated where comp is missing.' },
  { source: 'App', label: 'Overhead', detail: 'monthly overhead pool spread across billable hours' },
  { source: 'Derived', label: 'Net profit / margin', detail: 'commercial value − (hours × cost rate + overhead), per the active formula graph' },
];

const profitTone = (v) => ((v || 0) >= 0 ? 'text-emerald-600' : 'text-brand-500');

function StatusBadge({ status }) {
  const isActive = (status || '').toUpperCase() === 'ACTIVE';
  return <Pill tone={isActive ? 'ok' : 'neutral'}>{isActive ? 'Active' : status || 'Unknown'}</Pill>;
}

const COLS = [
  { label: 'Project',          key: 'project_name',             type: 'str' },
  { label: 'Client',           key: 'client_name',              type: 'str' },
  { label: 'Entity',           key: 'billing_entity_name',      type: 'str' },
  { label: 'Status',           key: 'status',                   type: 'str' },
  { label: 'Once-off',         key: 'once_off_amount',          type: 'num', align: 'right' },
  { label: 'Retainer / mo',    key: 'monthly_amount',           type: 'num', align: 'right' },
  { label: 'Revenue',          key: 'commercial_value',         type: 'num', align: 'right' },
  { label: 'Tracked billing',  key: 'total_billing_value',      type: 'num', align: 'right' },
  { label: 'Est. hours',       key: 'estimated_hours_snapshot', type: 'num', align: 'right' },
  { label: 'Tracked hours',    key: 'total_tracked_hours',      type: 'num', align: 'right',
    tip: 'Total hours logged in Magnetic this month across all devs (including users without a BambooHR record).' },
  { label: 'Net profit',       key: 'net_profit',               type: 'num', align: 'right' },
  { label: 'Margin %',         key: 'net_margin_pct',           type: 'num', align: 'right' },
];

export default function KPIDashboard() {
  const chart = {
    grid:    getComputedStyle(document.documentElement).getPropertyValue('--chart-grid')?.trim() || '#edecea',
    tick:    getComputedStyle(document.documentElement).getPropertyValue('--chart-tick')?.trim() || '#606067',
    tipBg:   getComputedStyle(document.documentElement).getPropertyValue('--tooltip-bg')?.trim() || '#ffffff',
    tipBord: getComputedStyle(document.documentElement).getPropertyValue('--tooltip-border')?.trim() || '#edecea',
    tipText: getComputedStyle(document.documentElement).getPropertyValue('--tooltip-text')?.trim() || '#202026',
  };

  const [months, setMonths]     = useState([]);
  const [month, setMonth]       = useState(null);
  const [period, setPeriod]     = useState('month'); // month | mtd | ytd | year
  const [summary, setSummary]   = useState(null);
  const [projects, setProjects] = useState([]);
  const [search, setSearch]     = useState('');
  const [entity, setEntity]     = useState('');   // billing entity id ('' = all)
  const [loading, setLoading]   = useState(true);
  const [sortCol, setSortCol]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');

  useEffect(() => {
    api.kpi.months().then((d) => {
      setMonths(d);
      if (d.length) setMonth(d[0].period_month);
    });
  }, []);

  const [year, setYear] = useState(null);

  // distinct years available, newest first
  const years = useMemo(() => {
    const ys = [...new Set(months.map((m) => m.period_month.slice(0, 4)))];
    return ys.sort().reverse();
  }, [months]);
  useEffect(() => { if (years.length && !year) setYear(years[0]); }, [years, year]);

  useEffect(() => {
    if (!month && period !== 'all' && period !== 'mtd') { setLoading(false); return; }
    setLoading(true);
    // ref anchors the period: month/ytd → picked month; year → picked year;
    // mtd → today (ignored); all → ignored.
    const ref = period === 'year' ? `${year}-01` : month;
    const ent = entity ? Number(entity) : undefined;
    const listParams = period === 'month'
      ? { month, billing_entity: ent }
      : { period, ref, billing_entity: ent };
    Promise.all([api.kpi.summary(period, ref, ent), api.projects.list(listParams)])
      .then(([s, p]) => { setSummary(s); setProjects(p); })
      .finally(() => setLoading(false));
  }, [month, period, year, entity]);

  const PERIODS = [
    ['month', 'Month'], ['mtd', 'MTD'], ['ytd', 'YTD'], ['year', 'Year'], ['all', 'All'],
  ];

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = projects
    .filter((r) =>
      !search ||
      (r.project_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (r.client_name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (!sortCol) return 0;
      const col = COLS.find((c) => c.key === sortCol);
      const av = a[sortCol] ?? (col?.type === 'str' ? '' : -Infinity);
      const bv = b[sortCol] ?? (col?.type === 'str' ? '' : -Infinity);
      const cmp = col?.type === 'str'
        ? av.toString().localeCompare(bv.toString())
        : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const barData = filtered
    .filter((r) => !r.is_unbilled)
    .slice(0, 20)
    .map((r) => ({
      name:   (r.project_name || '').split(':')[0].trim().slice(0, 20),
      margin: parseFloat((r.net_margin_pct || 0).toFixed(1)),
    }));

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title="Commercial Silo"
        subtitle="Profitability, utilisation and revenue across all live Platform45 projects."
        actions={
          <div className="flex items-end gap-3">
            <div className="flex bg-slate-800 border border-slate-700 rounded-md p-0.5 gap-0.5">
              {PERIODS.map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setPeriod(v)}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    period === v ? 'bg-brand-500 text-white' : 'text-slate-500 hover:text-slate-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {(period === 'month' || period === 'ytd') && (
              <FilterBar months={months} month={month} onMonth={setMonth} />
            )}
            {period === 'year' && (
              <Field label="Year">
                <Select value={year || ''} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </Select>
              </Field>
            )}
            <BillingEntityFilter value={entity} onChange={setEntity} />
          </div>
        }
      />
      {entity && (
        <p className="text-sm text-slate-400 -mt-4 mb-4 flex items-center gap-2">
          Scoped to <BillingEntityBadge name={projects[0]?.billing_entity_name} /> — headline numbers reflect this legal entity only.
        </p>
      )}
      {summary?.label && (
        <p className="text-sm text-slate-400 -mt-4 mb-6">
          Showing <span className="text-slate-200 font-medium">{summary.label}</span>
          {summary.as_of && <> · as of {summary.as_of}</>}
          {summary.estimated_count > 0 && (
            <> · <span className="text-amber-600">{summary.estimated_count} project(s) with estimated cost</span></>
          )}
        </p>
      )}

      <SourceNote items={KPI_SOURCES} />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <Stat
          label="Avg net margin"
          value={summary ? fmtPct(summary.avg_net_margin) : '—'}
          sub={`${summary?.project_count || 0} projects this month`}
          loading={loading}
        />
        <Stat
          label="Total revenue"
          value={summary ? fmtR(summary.total_revenue) : '—'}
          sub={`${summary?.unbilled_count || 0} projects unbilled`}
          loading={loading}
        />
        <Stat
          label="Total net profit"
          value={summary ? fmtR(summary.total_net_profit) : '—'}
          sub="Overhead deducted"
          loading={loading}
        />
        <Stat
          label="Avg utilisation"
          value={summary ? fmtPct(summary.avg_utilisation) : '—'}
          sub="Tracked ÷ capacity"
          loading={loading}
        />
      </div>

      {projects.length === 0 && !loading ? (
        <Card>
          <Empty>
            No profitability data yet. Capture overhead in Settings to unlock calculations.
          </Empty>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            <Card title="Net margin · top 20 projects" subtitle={month}>
              {barData.length === 0 ? (
                <Empty>No billed projects this month.</Empty>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barData} margin={{ top: 8, right: 12, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={chart.grid} vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: chart.tick, fontSize: 11 }}
                      angle={-35}
                      textAnchor="end"
                      interval={0}
                      axisLine={{ stroke: chart.grid }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: chart.tick, fontSize: 11 }}
                      unit="%"
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{ background: chart.tipBg, border: `1px solid ${chart.tipBord}`, borderRadius: 6, fontSize: 12 }}
                      labelStyle={{ color: chart.tipText, fontWeight: 500 }}
                      cursor={{ fill: 'rgba(233, 32, 48, 0.06)' }}
                      formatter={(v) => [`${v}%`, 'Net margin']}
                    />
                    <Bar dataKey="margin" fill="#e92030" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title={`Month summary · ${month}`}>
              {summary && (
                <dl className="space-y-3">
                  {[
                    ['Total revenue',     fmtR(summary.total_revenue)],
                    ['Total cost',        fmtR(summary.total_labour_cost)],
                    ['Total net profit',  fmtR(summary.total_net_profit)],
                    ['Avg net margin',    fmtPct(summary.avg_net_margin)],
                    ['Projects billed',   `${(summary.project_count || 0) - (summary.unbilled_count || 0)}`],
                    ['Projects unbilled', `${summary.unbilled_count || 0}`],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-baseline text-sm border-b border-slate-700 pb-2 last:border-b-0">
                      <dt className="text-slate-500">{label}</dt>
                      <dd className="text-slate-100 font-medium nums">{val}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </Card>
          </div>

          <Card
            title="All projects"
            subtitle={`${filtered.length} of ${projects.length}`}
            action={<FilterBar search={search} onSearch={setSearch} />}
            padding="p-0"
          >
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-slate-800">
                  <tr className="text-[11px] font-medium uppercase tracking-[0.10em] text-slate-500">
                    {COLS.map(({ label, key, tip, align }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className={`px-4 py-3 border-b border-slate-700 font-medium cursor-pointer select-none hover:text-slate-200 transition-colors text-${align || 'left'}`}
                      >
                        <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
                          {tip ? <Hint label={label}>{tip}</Hint> : label}
                          {sortCol === key ? (
                            sortDir === 'asc'
                              ? <ArrowUp className="w-3 h-3" strokeWidth={2} />
                              : <ArrowDown className="w-3 h-3" strokeWidth={2} />
                          ) : (
                            <ChevronsUpDown className="w-3 h-3 opacity-40" strokeWidth={2} />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
                  )}
                  {filtered.map((r) => (
                    <tr key={r.project_id} className="hover:bg-slate-700/40 transition-colors">
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-slate-100 font-medium max-w-[200px] truncate">{r.project_name}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-slate-400 max-w-[160px] truncate">{r.client_name || '—'}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50"><BillingEntityBadge name={r.billing_entity_name} /></td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-300 nums">{r.once_off_amount > 0 ? fmtR(r.once_off_amount) : '—'}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-300 nums">{r.monthly_amount > 0 ? fmtR(r.monthly_amount) : '—'}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-100 nums">
                        {r.is_unbilled ? <Pill tone="warn">Unbilled</Pill> : fmtR(r.commercial_value)}
                      </td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-300 nums">{fmtR(r.total_billing_value)}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-300 nums">{fmtH(r.estimated_hours_snapshot)}</td>
                      <td className="px-4 py-2.5 border-b border-slate-700/50 text-right text-slate-300 nums">{fmtH(r.total_tracked_hours)}</td>
                      <td className={`px-4 py-2.5 border-b border-slate-700/50 text-right font-medium nums ${profitTone(r.net_profit)}`}>
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          {r.cost_is_estimated && (
                            <span title="Labour cost is a 33% proxy — no captured CTC for one or more devs"
                                  className="text-[10px] font-medium text-amber-600 border border-amber-500/30 rounded px-1">est</span>
                          )}
                          {fmtR(r.net_profit)}
                        </span>
                      </td>
                      <td className={`px-4 py-2.5 border-b border-slate-700/50 text-right font-medium nums ${r.is_unbilled ? 'text-slate-500' : profitTone(r.net_margin_pct)}`}>
                        {r.is_unbilled ? '—' : fmtPct(r.net_margin_pct)}
                      </td>
                    </tr>
                  ))}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-slate-500">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
